import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  appointmentEditPeriodError,
  appointmentPeriodError,
  type Appointment,
  type AppointmentPatchBody,
  type TicketDetail,
  type TifluxAppointmentPending,
} from '@erp/shared';
import { CalendarClock, Clock, Headset, ListChecks, Pencil, Play, Square, Timer, Trash2 } from 'lucide-react';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Badge, Dialog, Spinner } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatMinutes } from '@/lib/utils';
import { ticketAppointmentsQuery, ticketTifluxPendingsQuery } from './tickets-queries';
import { AppointmentPendingCard, ErpEditedMark, erpEditedNote } from './appointment-tiflux';
import {
  useCreateAppointment,
  useDeleteAppointment,
  useEditAppointment,
  usePlayTimer,
  useStopTimer,
} from './tickets-mutations';
import { TemplateInsertMenu } from '@/features/models/template-insert-menu';
import { durationMinutes, errorMessage, initials, isTicketMirrored } from './ticket-format';
import { MirroredNotice } from './mirrored-badge';
import { DurationPill } from './detail-status-chip';
import { formatDate, formatElapsed, formatTime } from './detail-format';

/**
 * `datetime-local` no relógio do navegador — o MESMO em que o formulário lê o
 * que se digita (new Date de 'aaaa-mm-ddThh:mm' é hora local), então o valor
 * preenchido volta igual ao gravado. Os segundos ficam de fora do campo.
 */
function toDatetimeLocal(value: string | Date): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** por que a linha não se edita (null = edita) — o tooltip do lápis desligado */
function editBlockReason(a: Appointment, own: boolean): string | null {
  // só o dono para o próprio timer: para o de outra pessoa, o conselho é esperar
  if (a.endedAt === null)
    return own
      ? 'Timer em andamento — pare o timer antes de editar.'
      : 'Timer em andamento — dá para editar depois que ele for parado.';
  // ADR-013: no ticket de cliente operado no ERP o vinculado se edita (e a
  // edição vence o TiFlux); trava só o de cliente que não é operado
  if (a.tifluxLocked === true)
    return 'Veio do TiFlux e o cliente não é operado no ERP — corrija no TiFlux: a sincronização regravaria a edição feita aqui.';
  return null;
}

/** o formulário do apontamento offline: lançar um novo ou editar um existente */
type FormTarget = { mode: 'create' } | { mode: 'edit'; appointment: Appointment };

/** Aba "Apontamentos": KPIs + timer play/stop + apontamento offline + tabela. */
export function ApontamentosTab({ ticket }: { ticket: TicketDetail }) {
  const user = useAuthUser();
  const { can, ready: permsReady } = usePermissions();
  const apptsQ = useQuery(ticketAppointmentsQuery(ticket.ticketNumber));
  const play = usePlayTimer(ticket.ticketNumber);
  const stop = useStopTimer(ticket.ticketNumber);
  const createAppt = useCreateAppointment(ticket.ticketNumber);
  const editAppt = useEditAppointment(ticket.ticketNumber);
  const deleteAppt = useDeleteAppointment(ticket.ticketNumber);

  const [stopOpen, setStopOpen] = useState(false);
  const [stopDescription, setStopDescription] = useState('');
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Appointment | null>(null);
  const [pendingView, setPendingView] = useState<TifluxAppointmentPending | null>(null);
  const [error, setError] = useState<string | null>(null);

  const appointments = apptsQ.data ?? [];
  const myTimerRunning = user !== null && ticket.runningTimerUserIds.includes(user.id);
  const otherTimers = ticket.runningTimerUserIds.filter((id) => id !== user?.id).length;
  const isCanceled = ticket.status.kind === 'canceled';
  // spec 011: apontar (timer ou manual) e excluir são escritas recusadas em
  // ticket espelhado — os apontamentos de lá chegam pelo sync
  const mirrored = isTicketMirrored(ticket);
  // ADR-013: as pendências de APONTAMENTO (editado no ERP, mudado no TiFlux
  // depois) — a mesma consulta do indicador do cabeçalho; só fora do espelho
  const pendingsQ = useQuery({
    ...ticketTifluxPendingsQuery(ticket.ticketNumber),
    enabled: ticket.mirrorDetachedAt !== null && !mirrored,
  });
  const appointmentPendings = useMemo(
    () =>
      new Map(
        (pendingsQ.data ?? []).flatMap((p) =>
          p.field === 'appointment' ? [[p.appointmentId, p] as const] : []
        )
      ),
    [pendingsQ.data]
  );

  const myRunning = useMemo(
    () => appointments.find((a) => a.endedAt === null && a.user.id === user?.id) ?? null,
    [appointments, user?.id]
  );

  const attendants = useMemo(
    () => new Set(appointments.map((a) => a.user.id)).size,
    [appointments]
  );

  /**
   * Quem pode editar/excluir a linha — a MESMA regra da API, só por permissão
   * (v7 §1, sem literal de role): o próprio apontamento pede
   * appointments.create_edit; o de outra pessoa, appointments.edit_all.
   * Enquanto o /me carrega, esconde (aparecer depois é melhor que sumir).
   */
  const mayChange = (a: Appointment) =>
    !mirrored &&
    user !== null &&
    permsReady &&
    can(a.user.id === user.id ? 'appointments.create_edit' : 'appointments.edit_all');

  const openCreate = () => {
    setError(null);
    setManualStart('');
    setManualEnd('');
    setManualDescription('');
    setFormTarget({ mode: 'create' });
  };

  const openEdit = (a: Appointment) => {
    if (a.endedAt === null) return;
    setError(null);
    setManualStart(toDatetimeLocal(a.startedAt));
    setManualEnd(toDatetimeLocal(a.endedAt));
    setManualDescription(a.description ?? '');
    setFormTarget({ mode: 'edit', appointment: a });
  };

  const submitStop = () => {
    setError(null);
    stop.mutate(stopDescription.trim() || null, {
      onSuccess: () => {
        setStopOpen(false);
        setStopDescription('');
      },
      onError: (e) => setError(errorMessage(e)),
    });
  };

  const closeForm = () => {
    setFormTarget(null);
    setManualStart('');
    setManualEnd('');
    setManualDescription('');
  };

  const submitForm = () => {
    if (!formTarget) return;
    setError(null);
    const start = new Date(manualStart);
    const end = new Date(manualEnd);
    if (!manualStart || !manualEnd || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError('Informe início e fim do apontamento.');
      return;
    }
    const onError = (e: unknown) => setError(errorMessage(e));

    if (formTarget.mode === 'create') {
      // à mão: a regra da criação inteira (nunca duração zero)
      if (appointmentPeriodError(start, end) !== null) {
        setError('O fim deve ser depois do início.');
        return;
      }
      createAppt.mutate(
        {
          startedAt: start.toISOString(),
          endedAt: end.toISOString(),
          description: manualDescription.trim() || undefined,
        },
        { onSuccess: closeForm, onError }
      );
      return;
    }

    // edição: só vai o que a pessoa mudou. O campo não tem segundos, então
    // reenviar um horário intocado cortaria os segundos de um apontamento de
    // timer e registraria uma "mudança" que ninguém fez.
    const a = formTarget.appointment;
    if (a.endedAt === null) return;
    const body: AppointmentPatchBody = {};
    if (manualStart !== toDatetimeLocal(a.startedAt)) body.startedAt = start.toISOString();
    if (manualEnd !== toDatetimeLocal(a.endedAt)) body.endedAt = end.toISOString();
    // o período que vai valer: o que mudou + o que ficou, pela MESMA regra da
    // API — que não rejulga o período que a edição não muda (o 1º contato do
    // disparo tem duração zero e a descrição dele se corrige)
    const effStart = body.startedAt ? start : new Date(a.startedAt);
    const effEnd = body.endedAt ? end : new Date(a.endedAt);
    if (
      appointmentEditPeriodError(
        { startedAt: a.startedAt, endedAt: a.endedAt },
        { startedAt: effStart, endedAt: effEnd }
      ) !== null
    ) {
      setError('O fim deve ser depois do início.');
      return;
    }
    const description = manualDescription.trim() || null;
    if (description !== (a.description ?? null)) body.description = description;
    if (Object.keys(body).length === 0) {
      closeForm();
      return;
    }
    editAppt.mutate({ id: a.id, body }, { onSuccess: closeForm, onError });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Atendentes" icon={<Headset className="size-5" />} value={attendants} />
        <StatCard
          label="Horas"
          icon={<Clock className="size-5" />}
          value={formatMinutes(ticket.workedMinutes)}
        />
        <StatCard
          label="Apontamentos"
          icon={<ListChecks className="size-5" />}
          value={appointments.length}
        />
        <StatCard
          label="Timers ativos"
          icon={<Timer className="size-5" />}
          value={ticket.runningTimerUserIds.length}
          highlight={ticket.runningTimerUserIds.length > 0}
        />
      </div>

      {mirrored ? (
        <MirroredNotice>
          Ticket espelhado do TiFlux: aponte as horas no TiFlux. Os apontamentos de lá aparecem aqui
          na próxima sincronização.
        </MirroredNotice>
      ) : (
        <section className="flex flex-wrap items-center gap-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
          {myTimerRunning ? (
            <button
              type="button"
              title="Parar apontamento"
              onClick={() => {
                setError(null);
                setStopOpen(true);
              }}
              className="flex size-12 shrink-0 items-center justify-center rounded-full bg-destructive text-white shadow-sm transition-colors hover:bg-destructive/90 focus-visible:ring-2 focus-visible:ring-destructive/40 focus-visible:outline-none"
            >
              <Square className="size-5 fill-current" />
            </button>
          ) : (
            <button
              type="button"
              title="Iniciar apontamento"
              disabled={play.isPending || isCanceled}
              onClick={() => play.mutate()}
              className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              {play.isPending ? (
                <Spinner className="size-5 border-primary-foreground" />
              ) : (
                <Play className="ml-0.5 size-5 fill-current" />
              )}
            </button>
          )}

          <div className="min-w-0">
            {myTimerRunning ? (
              <>
                <ElapsedClock startedAt={myRunning?.startedAt ?? null} />
                <p className="text-xs text-muted-foreground">Apontamento em andamento</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold">Iniciar apontamento</p>
                <p className="text-xs text-muted-foreground">
                  {isCanceled
                    ? 'Ticket cancelado — reabra para apontar horas.'
                    : 'Registre o tempo trabalhado neste ticket.'}
                </p>
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {otherTimers > 0 ? (
              <span className="text-xs text-muted-foreground">
                {otherTimers === 1
                  ? '1 timer de outro usuário em andamento'
                  : `${otherTimers} timers de outros usuários em andamento`}
              </span>
            ) : null}
            <Button variant="outline" size="sm" onClick={openCreate}>
              <CalendarClock className="text-muted-foreground" />
              Apontamento offline
            </Button>
          </div>

          {play.isError ? (
            <p className="w-full text-sm text-destructive">{errorMessage(play.error)}</p>
          ) : null}
        </section>
      )}

      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
        <h2 className="border-b p-4 text-base font-semibold">Apontamentos</h2>
        {apptsQ.isPending ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : apptsQ.isError ? (
          <p className="p-4 text-sm text-muted-foreground">Erro ao carregar apontamentos.</p>
        ) : appointments.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Nenhum apontamento registrado.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="px-4 text-xs font-semibold">Data</TableHead>
                <TableHead className="text-xs font-semibold">Início</TableHead>
                <TableHead className="text-xs font-semibold">Fim</TableHead>
                <TableHead className="text-xs font-semibold">Duração</TableHead>
                <TableHead className="text-xs font-semibold">Usuário</TableHead>
                <TableHead className="w-20">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.map((a) => {
                const running = a.endedAt === null;
                const canChange = mayChange(a);
                const blocked = editBlockReason(a, a.user.id === user?.id);
                const edited = erpEditedNote(a);
                const pending = appointmentPendings.get(a.id);
                // spec 066 (D4): a descrição inteira numa linha logo abaixo,
                // como no TiFlux, sem abrir a edição para ler
                const hasNotes = Boolean(a.description || edited || pending);
                return [
                  <TableRow key={a.id} className={cn('h-10', hasNotes && 'border-b-0')}>
                    <TableCell className="px-4 text-sm tabular-nums">
                      {formatDate(a.startedAt)}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{formatTime(a.startedAt)}</TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {running ? '—' : formatTime(a.endedAt as string | Date)}
                    </TableCell>
                    <TableCell>
                      {running ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                          <Timer className="size-3 animate-pulse" />
                          Em andamento
                        </span>
                      ) : (
                        <DurationPill>
                          {formatMinutes(durationMinutes(a.startedAt, a.endedAt as string | Date))}
                        </DurationPill>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <Avatar size="sm">
                          <AvatarFallback className="bg-primary/15 text-[10px] font-semibold text-primary">
                            {initials(a.user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="max-w-40 truncate text-sm">{a.user.name}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {canChange ? (
                        <span className="inline-flex items-center justify-end gap-1">
                          {blocked ? (
                            <Tooltip>
                              {/* botão desligado não recebe o mouse: o gatilho é o span */}
                              <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex" aria-label={blocked}>
                                  <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    className="text-muted-foreground"
                                    disabled
                                    aria-label="Editar apontamento"
                                  >
                                    <Pencil />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-64">
                                {blocked}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              className="text-muted-foreground hover:text-foreground"
                              title="Editar apontamento"
                              aria-label="Editar apontamento"
                              onClick={() => openEdit(a)}
                            >
                              <Pencil />
                            </Button>
                          )}
                          {!running ? (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              className="text-muted-foreground hover:text-destructive"
                              title="Excluir apontamento"
                              aria-label="Excluir apontamento"
                              onClick={() => setDeleteTarget(a)}
                            >
                              <Trash2 />
                            </Button>
                          ) : null}
                        </span>
                      ) : null}
                    </TableCell>
                  </TableRow>,
                  hasNotes ? (
                    <TableRow
                      key={`${a.id}-descricao`}
                      className="hover:bg-transparent"
                      data-tour="apontamento-descricao"
                    >
                      <TableCell colSpan={6} className="px-4 pt-0 pb-3 whitespace-normal">
                        <p className="text-xs font-semibold text-muted-foreground">Descrição</p>
                        <p
                          className={cn(
                            'text-sm break-words whitespace-pre-wrap',
                            !a.description && 'text-muted-foreground'
                          )}
                        >
                          {a.description || '—'}
                        </p>
                        {edited || pending ? (
                          <span className="mt-1 flex items-center gap-1.5">
                            {edited ? <ErpEditedMark note={edited} /> : null}
                            {pending ? (
                              <button
                                type="button"
                                className="inline-flex shrink-0"
                                onClick={() => setPendingView(pending)}
                                title="Editado no ERP e mudado no TiFlux depois — clique para ver"
                              >
                                <Badge variant="warning">mudou no TiFlux</Badge>
                              </button>
                            ) : null}
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ) : null,
                ];
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {stopOpen ? (
        <Dialog open onClose={() => setStopOpen(false)} title="Parar apontamento">
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="stop-description">O que foi feito?</Label>
                <TemplateInsertMenu
                  kind="appointments"
                  ticket={ticket}
                  onInsert={(text) =>
                    setStopDescription((prev) => (prev.trim() ? `${prev}\n${text}` : text))
                  }
                />
              </div>
              <Textarea
                id="stop-description"
                value={stopDescription}
                onChange={(e) => setStopDescription(e.target.value)}
                placeholder="Descrição do atendimento…"
                autoFocus
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setStopOpen(false)}>
                Voltar
              </Button>
              <Button size="sm" onClick={submitStop} disabled={stop.isPending}>
                {stop.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
                Parar apontamento
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {formTarget ? (
        <Dialog
          open
          onClose={closeForm}
          title={formTarget.mode === 'edit' ? 'Editar apontamento' : 'Apontamento offline'}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="manual-start">Início</Label>
                <Input
                  id="manual-start"
                  type="datetime-local"
                  value={manualStart}
                  onChange={(e) => setManualStart(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="manual-end">Fim</Label>
                <Input
                  id="manual-end"
                  type="datetime-local"
                  value={manualEnd}
                  onChange={(e) => setManualEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="manual-description">Descrição</Label>
                <TemplateInsertMenu
                  kind="appointments"
                  ticket={ticket}
                  onInsert={(text) =>
                    setManualDescription((prev) => (prev.trim() ? `${prev}\n${text}` : text))
                  }
                />
              </div>
              <Textarea
                id="manual-description"
                value={manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                placeholder="Descrição do atendimento…"
              />
            </div>
            {formTarget.mode === 'edit' && formTarget.appointment.fromTiflux === true ? (
              <p className="text-xs text-muted-foreground">
                Veio do TiFlux. Editado aqui, passa a valer o do ERP: a sincronização não o regrava
                mais, e se alguém o mudar no TiFlux depois, vira pendência para decidir.
              </p>
            ) : null}
            {formTarget.mode === 'edit' && formTarget.appointment.user.id !== user?.id ? (
              <p className="text-xs text-muted-foreground">
                Apontamento de {formTarget.appointment.user.name}. A edição fica no histórico do ticket.
              </p>
            ) : null}
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closeForm}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={submitForm}
                disabled={createAppt.isPending || editAppt.isPending}
              >
                {createAppt.isPending || editAppt.isPending ? (
                  <Spinner className="size-4 border-primary-foreground" />
                ) : null}
                {formTarget.mode === 'edit' ? 'Salvar' : 'Lançar'}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {pendingView ? (
        <Dialog open onClose={() => setPendingView(null)} title="Apontamento mudou no TiFlux">
          <div className="min-w-0 space-y-3 text-sm contain-[inline-size]">
            <p className="text-muted-foreground">
              Este apontamento foi editado no ERP e, depois, alguém o mudou no TiFlux. Nada foi
              sobrescrito: vale o do ERP até alguém decidir.
            </p>
            <AppointmentPendingCard pending={pendingView} />
            <p className="text-muted-foreground">
              Um administrador escolhe entre manter o do ERP e aceitar o do TiFlux em{' '}
              <Link
                to="/clients/$clientId"
                params={{ clientId: String(ticket.client.id) }}
                className="text-primary hover:underline"
              >
                {ticket.client.name}
              </Link>{' '}
              → Puxar do TiFlux.
            </p>
          </div>
        </Dialog>
      ) : null}

      {deleteTarget ? (
        <Dialog open onClose={() => setDeleteTarget(null)} title="Excluir apontamento">
          <p className="text-sm text-muted-foreground">
            Excluir o apontamento de {formatDate(deleteTarget.startedAt)} (
            {formatTime(deleteTarget.startedAt)}
            {deleteTarget.endedAt ? ` - ${formatTime(deleteTarget.endedAt)}` : ''})? Esta ação não
            pode ser desfeita.
          </p>
          {deleteAppt.isError ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(deleteAppt.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteAppt.isPending}
              onClick={() =>
                deleteAppt.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })
              }
            >
              Excluir
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function StatCard({
  label,
  icon,
  value,
  highlight,
}: {
  label: string;
  icon: React.ReactNode;
  value: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <div
        className={cn(
          'mt-1 flex items-center gap-2 text-xl font-bold tabular-nums',
          highlight && 'text-primary'
        )}
      >
        <span className={cn('text-muted-foreground', highlight && 'text-primary')}>{icon}</span>
        {value}
      </div>
    </div>
  );
}

/** Relógio HH:MM:SS que atualiza a cada segundo enquanto o timer roda. */
function ElapsedClock({ startedAt }: { startedAt: string | Date | null }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!startedAt) return <p className="text-sm font-semibold">Em andamento</p>;
  const start = new Date(startedAt).getTime();
  return (
    <p className="text-lg leading-tight font-bold text-destructive tabular-nums">
      {formatElapsed(now - start)}
    </p>
  );
}
